import type { ReactNode } from 'react'
import type { LucideIcon } from '@/ui/icons'
import { DEFAULT_ICON_CHIP } from './feedCardStyles'

interface FeedRowProps {
  title: string
  subtitle?: ReactNode
  /** Row-leading icon, mapped from the card's semantic `icon` key. */
  icon?: LucideIcon
  /** Background/foreground classes for the icon chip. Defaults to a neutral earth tone. */
  iconChipClassName?: string
  /** Optional badge (e.g. priority, "Ranger", "Pro"). */
  badge?: ReactNode
  /** Right-aligned action element (Button/Link). */
  action?: ReactNode
}

/**
 * A single feed item row: icon chip + title/subtitle + badge + action, divided by a
 * hairline border. Tint only appears on hover, keeping the list quiet at rest.
 */
export function FeedRow({ title, subtitle, icon: Icon, iconChipClassName, badge, action }: FeedRowProps) {
  return (
    <li className="flex items-center gap-3 p-3 border-b border-border last:border-0 hover:bg-earth-50 dark:hover:bg-earth-800/50 rounded-lg transition-colors">
      {Icon && (
        <div
          className={`flex-shrink-0 w-9 h-9 rounded-md flex items-center justify-center ${iconChipClassName || DEFAULT_ICON_CHIP}`}
        >
          <Icon className="w-4 h-4" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-earth-900 dark:text-foreground truncate">{title}</p>
        {subtitle && (
          <p className="text-xs text-earth-700 dark:text-earth-400 line-clamp-2">{subtitle}</p>
        )}
      </div>

      {badge && <div className="flex-shrink-0">{badge}</div>}
      {action && <div className="flex-shrink-0">{action}</div>}
    </li>
  )
}
